import { Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

export const InterceptorSkipHeader = 'X-Skip-Interceptor';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    /**
     * Constructor
     */
    constructor(private _authService: AuthService, private router: Router) {}

    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // add condition for public apis
        if (req.headers.get(InterceptorSkipHeader)) {
            const headers = req.headers.delete(InterceptorSkipHeader);
            return next.handle(req.clone({ headers }));
        }

        // Clone the request object
        let newReq = req.clone();

        // Request
        //
        // If the access token didn't expire, add the Authorization header.
        // We won't add the Authorization header if the access token expired.
        // This will force the server to return a "401 Unauthorized" response
        // for the protected API routes which our response interceptor will
        // catch and delete the access token from the local storage while logging
        // the user out from the app.

        if (this._authService.accessToken) {
            if (req.url.indexOf('auth/imsme/logout') === -1) {
                newReq = req.clone({
                    headers: req.headers
                        .set(
                            'Authorization',
                            'Bearer ' + this._authService.accessToken
                        )
                        .set('idToken', this._authService.getIdTokenLocal()),
                });
            } else {
                newReq = req.clone({
                    headers: req.headers.set(
                        'Authorization',
                        'Bearer ' + this._authService.accessToken
                    ),
                });
            }
        }

        // Response
        return next.handle(newReq).pipe(
            catchError((error) => {
                // Catch "401 Unauthorized" responses
                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 401
                ) {
                    // Attempt to refresh the access token
                    return this._authService.refreshAccessToken().pipe(
                        switchMap(() => {
                            // Retry the request with the new token
                            newReq = req.clone({
                                headers: req.headers
                                    .set(
                                        'Authorization',
                                        'Bearer ' +
                                            this._authService.accessToken
                                    )
                                    .set(
                                        'Idtoken',
                                        localStorage.getItem('idToken')
                                    ),
                            });
                            return next.handle(newReq);
                        }),
                        catchError((refreshError) => {
                            // Handle refresh token failure (e.g., redirect to login page)
                            console.error(
                                'Failed to refresh access token',
                                refreshError
                            );
                            this._authService.signOut();
                            this.router.navigate(['/login']);
                            return throwError(refreshError);
                        })
                    );
                }

                return throwError(error);
            })
        );
    }
}
